import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { UseCases } from "@planzone/core";
import classNames from "classnames";

import {
  useRetrieveCurrentPlanzone,
  useRetrieveZonesProjectsReports,
} from "@planzone/view-models";
import { Identifiable, ZoneProjectReport } from "@planzone/dto";
import { usePreferences } from "@components/preferences";
import { Filter as F } from "@planzone/dto";
import { Filter } from "@common/filter";
import { useModal } from "@root/client/modal";
import { Report } from "./report";
import { Loader } from "./loader";
import { Toolbar } from "./toolbar";
import { Props, SortOptions } from "./types";
import styles from "./styles.scss";

function getWeek(date: Date) {
  const janFirst = new Date(date.getFullYear(), 0, 1);

  return Math.ceil(
    ((date.getTime() - janFirst.getTime()) / 86400000 + janFirst.getDay() + 1) /
      7,
  );
}

function isSameWeek(firstDate: Date, secondDate: Date) {
  return getWeek(firstDate) === getWeek(secondDate);
}

function sortReports(reports: ZoneProjectReport[]) {
  const now = new Date();
  const isSubmittedThisWeek = ({ report }: ZoneProjectReport) =>
    isSameWeek(new Date(report.submissionDate), now);
  const isSubmittedThisMonth = ({ report }: ZoneProjectReport) =>
    new Date(report.submissionDate).getMonth() === now.getMonth() &&
    new Date(report.submissionDate).getFullYear() === now.getFullYear();

  return reports.reduce<{
    week: ZoneProjectReport[];
    month: ZoneProjectReport[];
    others: ZoneProjectReport[];
  }>(
    (sortedReports, report) =>
      isSubmittedThisWeek(report)
        ? {
            ...sortedReports,
            week: [...sortedReports.week, report],
          }
        : isSubmittedThisMonth(report)
          ? {
              ...sortedReports,
              month: [...sortedReports.month, report],
            }
          : {
              ...sortedReports,
              others: [...sortedReports.others, report],
            },
    {
      week: [],
      month: [],
      others: [],
    },
  );
}

function orderReports(reports: ZoneProjectReport[]): ZoneProjectReport[] {
  return reports.sort((current, next) =>
    new Date(current.report.submissionDate) <
    new Date(next.report.submissionDate)
      ? 1
      : -1,
  );
}

function ReportsPage(props: Props) {
  const { t } = useTranslation();
  const { planzone } = useRetrieveCurrentPlanzone();
  const { pushView } = useModal();
  const {
    reports,
    isRequestPending,
    isRequestSuccess,
    isRequestFailure,
    retrieveZonesProjectsReports,
  } = useRetrieveZonesProjectsReports();
  const { state, edit } = usePreferences();
  const { zonesProjectReportsFilter: filter } = state;
  const setFilter = (nextFilter: F) =>
    edit({ ...state, zonesProjectReportsFilter: nextFilter });
  const sortedReports = sortReports(reports);
  const [sort, setSelectedSorter] = useState<SortOptions>(SortOptions.latest);

  React.useEffect(() => {
    !isRequestPending &&
      !isRequestSuccess &&
      !isRequestFailure &&
      retrieveZonesProjectsReports(planzone);
  }, [planzone.uuid]);

  return (
    <div className={styles.reportsPage}>
      <Toolbar
        sort={sort}
        filter={filter}
        setSort={setSelectedSorter}
        setFilter={setFilter}
      />
      {isRequestPending ? (
        <div className={styles.main}>
          <div className={styles.section}>
            <h3 className={styles.title}>
              {t("zonesPage.reportsPage.thisWeek")}
            </h3>
            <div className={styles.reportsList}>
              <Loader />
            </div>
          </div>
          <div className={styles.section}>
            <h3 className={styles.title}>
              {t("zonesPage.reportsPage.thisWeek")}
            </h3>
            <div className={styles.reportsList}>
              <Loader />
            </div>
          </div>
          <div className={styles.section}>
            <h3 className={styles.title}>
              {t("zonesPage.reportsPage.thisMonth")}
            </h3>
            <div className={styles.reportsList}>
              <Loader />
            </div>
          </div>
          <div className={styles.section}>
            <h3 className={styles.title}>
              {t("zonesPage.reportsPage.others")}
            </h3>
            <div className={styles.reportsList}>
              <Loader />
            </div>
          </div>
        </div>
      ) : (
        <div
          className={classNames(styles.main, {
            [styles.reverse]: sort === SortOptions.oldest,
          })}
        >
          <Filter
            data={orderReports(sortedReports.week)}
            filter={filter}
            extractCriteria={({ report, project }) => ({
              projectReport: report,
              project,
            })}
          >
            {(reports: Identifiable<ZoneProjectReport>[]) =>
              reports.length > 0 ? (
                <div className={styles.section}>
                  <h3 className={styles.title}>
                    {t("zonesPage.reportsPage.thisWeek")}
                  </h3>
                  <div className={styles.reportsList}>
                    {reports.map((report) => (
                      <div
                        key={report.report.uuid}
                        className={styles.report}
                        onClick={() =>
                          pushView({
                            data: {
                              project: report.project,
                              reportId: report.report.uuid,
                              isMultiProject: true,
                            },
                            key: UseCases.retrieveProjectReport,
                          })
                        }
                      >
                        <Report {...report} />
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <React.Fragment />
              )
            }
          </Filter>
          <Filter
            data={orderReports(sortedReports.month)}
            filter={filter}
            extractCriteria={({ report, project }) => ({
              projectReport: report,
              project,
            })}
          >
            {(reports: Identifiable<ZoneProjectReport>[]) =>
              reports.length > 0 ? (
                <div className={styles.section}>
                  <h3 className={styles.title}>
                    {t("zonesPage.reportsPage.thisMonth")}
                  </h3>
                  <div className={styles.reportsList}>
                    {reports.map((report) => (
                      <div
                        key={report.report.uuid}
                        className={styles.report}
                        onClick={() =>
                          pushView({
                            data: {
                              project: report.project,
                              reportId: report.report.uuid,
                              isMultiProject: true,
                            },
                            key: UseCases.retrieveProjectReport,
                          })
                        }
                      >
                        <Report {...report} />
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <React.Fragment />
              )
            }
          </Filter>
          <Filter
            data={orderReports(sortedReports.others)}
            filter={filter}
            extractCriteria={({ report, project }) => ({
              projectReport: report,
              project,
            })}
          >
            {(reports: Identifiable<ZoneProjectReport>[]) =>
              reports.length > 0 ? (
                <div className={styles.section}>
                  <h3 className={styles.title}>
                    {t("zonesPage.reportsPage.others")}
                  </h3>
                  <div className={styles.reportsList}>
                    {reports.map((report) => (
                      <div
                        key={report.report.uuid}
                        className={styles.report}
                        onClick={() =>
                          pushView({
                            data: {
                              project: report.project,
                              reportId: report.report.uuid,
                              isMultiProject: true,
                            },
                            key: UseCases.retrieveProjectReport,
                          })
                        }
                      >
                        <Report {...report} />
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <React.Fragment />
              )
            }
          </Filter>
        </div>
      )}
    </div>
  );
}

export { ReportsPage };
